import { toast } from '@/components/ui/use-toast'
import { useCreateEstateMedia } from '@/estateMedia/hooks/useCreateEstateMedia'
import {
  MultipleFileUploadDialog,
  MultipleFileUploadDialogProps,
} from '@/file/MultipleFileUploadDialog'
import {
  Estate_Media_Type_Name_Enum,
  EstateMediaTypeFragment,
} from '@gql'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EstateMediaTypeNameTranslation } from './EstateMediaTypeNameTranslation'

export type EstateMediaMultipleFileUploadDialogProps = Omit<
  MultipleFileUploadDialogProps,
  'onFileUpload' | 'onDone'
> & {
  children?: ReactNode
  onUpload: () => void
  type?: EstateMediaTypeFragment
  estateId: string
  showToasts?: boolean
}
export const EstateMediaMultipleFileUploadDialog = ({
  children,
  onUpload,
  type,
  estateId,
  showToasts,
  ...props
}: EstateMediaMultipleFileUploadDialogProps) => {
  const { t } = useTranslation()
  const { createEstateMedia, data, loading, error } = useCreateEstateMedia()
  const [isQueueProcessing, setIsQueueProcessing] = useState(false)
  const uploadQueueRef = useRef<Promise<void>>(Promise.resolve())
  const pendingCreateCountRef = useRef(0)
  const isPhotoType = useMemo(
    () => type?.name === Estate_Media_Type_Name_Enum.Photo,
    [type?.name]
  )

  const createEstateMediaEntry = async (id: string) => {
    if (!type?.id) {
      throw new Error('Missing estate media type')
    }

    await createEstateMedia({
      variables: {
        values: {
          fileId: id,
          typeId: type.id,
          estateId: estateId,
        },
      },
    })
  }

  const handleFileUpload = async (id: string) => {
    if (!id) {
      toast({
        variant: 'destructive',
        description: t('EstateMediaFileUploadDialog.toast.error'),
      })
      return
    }

    if (!isPhotoType) {
      await createEstateMediaEntry(id)
      return
    }

    pendingCreateCountRef.current += 1
    setIsQueueProcessing(true)
    // Photos use unique (estateId, typeId, position): serialize creation to reduce position conflicts.
    uploadQueueRef.current = uploadQueueRef.current.finally(async () => {
      try {
        await createEstateMediaEntry(id)
      } finally {
        pendingCreateCountRef.current -= 1
        if (pendingCreateCountRef.current <= 0) {
          setIsQueueProcessing(false)
        }
      }
    })
  }

  const handleDone = () => {
    if (!isPhotoType) {
      onUpload()
      return
    }

    void uploadQueueRef.current.finally(() => {
      onUpload()
    })
  }

  useEffect(() => {
    if (showToasts && error) {
      toast({
        variant: 'destructive',
        description: t('EstateMediaMultipleFileUploadDialog.toast.error'),
      })
    }
  }, [error])

  useEffect(() => {
    if (showToasts && data) {
      toast({
        variant: 'success',
        description: t('EstateMediaMultipleFileUploadDialog.toast.success'),
      })
    }
  }, [data])

  return (
    <MultipleFileUploadDialog
      description={
        type ? <EstateMediaTypeNameTranslation type={type} /> : undefined
      }
      onFileUpload={handleFileUpload}
      onDone={handleDone}
      loading={loading || isQueueProcessing}
      {...props}
    >
      {children}
    </MultipleFileUploadDialog>
  )
}
